import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import startCase from 'lodash/startCase'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { toast } from 'react-toastify'

import { CREATE, GET_LIST, GET_ONE, useClient } from '@peracto/client'
import { useConfig } from '@peracto/peracto-config'
import { Heading, HydraTable, TimeAgo } from '@peracto/peracto-ui'

import {
    DEFAULT_ERROR_MESSAGE,
    POLL_INTERVAL_MS,
    PROMOTIONS_API_IDENTIFIER,
    formatPromotionsDataTypeLabel,
    isCreatedWithinLast24Hours,
} from '../shared'

const PROMOTIONS_EXPORT_SUCCESS_MESSAGE = 'Promotions export started successfully!'

const PromotionsDataExportsList = () => {
    const { client } = useClient()
    const config = useConfig()
    const formatDate = config.get('user_format_date', config.get('format_date'))
    const [creatingExport, setCreatingExport] = useState(false)
    const [tableKey, setTableKey] = useState(0)
    const [listRows, setListRows] = useState([])

    const [selectedColumns, onSelectColumn] = useState({})

    const shouldPoll = useMemo(
        () =>
            listRows.some(
                (row) => row?.status === 'processing' && isCreatedWithinLast24Hours(row?.createdAt)
            ),
        [listRows]
    )

    const downloadExport = useCallback(
        async (id) => {
            try {
                const { data: downloadData } = await client(GET_ONE, 'export-logs', {
                    id: `/export-logs/${id}/pre-signed-url`,
                })

                if (downloadData?.downloadUrl) {
                    window.location.href = downloadData.downloadUrl
                } else {
                    toast.error(DEFAULT_ERROR_MESSAGE)
                }
            } catch (e) {
                console.error(e)
                const status = e?.status ?? e?.error?.status ?? e?.response?.status
                const hydraDescription = e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : null

                if (status === 400) {
                    toast.error(
                        hydraDescription ?? 'Export is not ready to download yet. Please try again.'
                    )
                } else {
                    toast.error(hydraDescription ?? DEFAULT_ERROR_MESSAGE)
                }
            }
        },
        [client]
    )

    const columns = useMemo(
        () => [
            {
                Header: 'ID',
                id: 'exportId',
                accessor: 'originId',
                disableSorting: true,
                Cell: ({ cell: { value }, row }) => (
                    <span data-testid={`row-${row.index}-id`}>{value}</span>
                ),
                selectable: false,
            },
            {
                Header: 'Type',
                accessor: 'type',
                sortType: 'basic',
                Cell: ({ cell: { value } }) => formatPromotionsDataTypeLabel(value),
                selectable: false,
                disableSorting: true,
            },
            {
                Header: 'Status',
                accessor: 'status',
                disableSorting: true,
                Cell: ({ cell: { value } }) => (
                    <span
                        className={classNames('badge', 'badge-pill', {
                            'badge-info': value === 'processing',
                            'badge-success': value === 'complete',
                            'badge-danger': value === 'error',
                        })}
                    >
                        {startCase(value)}
                    </span>
                ),
                selectable: false,
            },
            {
                Header: 'Row Count',
                accessor: 'rowCount',
                disableSorting: true,
                Cell: ({ cell: { value } }) => (value != null ? value : '-'),
                selectable: false,
            },
            {
                Header: 'Created',
                accessor: 'createdAt',
                disableSorting: true,
                Cell: ({ cell: { value } }) => {
                    if (!value) return '-'
                    const date = new Date(value)
                    if (!date.getDate()) return '-'
                    return <TimeAgo title={formatDate(date)} date={value} />
                },
                selectable: false,
            },
            {
                Header: 'Actions',
                id: 'actions',
                accessor: () => null,
                disableSorting: true,
                Cell: ({ row }) => {
                    if (row.original?.status === 'complete') {
                        return (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => downloadExport(row.original.originId)}
                                data-testid={`row-${row.index}-download`}
                            >
                                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                                Download
                            </button>
                        )
                    }
                    return '-'
                },
                selectable: false,
            },
        ],
        [formatDate, downloadExport]
    )

    const onFetchData = useCallback(
        async (options) => {
            const result = await client(GET_LIST, 'export-logs', options)
            setListRows(Array.isArray(result?.data) ? result.data : [])
            return result
        },
        [client]
    )

    useEffect(() => {
        if (!shouldPoll) return undefined

        const id = setInterval(() => {
            setTableKey((k) => k + 1)
        }, POLL_INTERVAL_MS)

        return () => clearInterval(id)
    }, [shouldPoll])

    const createExport = async () => {
        setCreatingExport(true)
        try {
            await client(CREATE, 'export-logs', {
                data: {
                    type: PROMOTIONS_API_IDENTIFIER,
                },
            })
            toast.success(PROMOTIONS_EXPORT_SUCCESS_MESSAGE)
            setTableKey((k) => k + 1)
        } catch (e) {
            console.error(e)
            toast.error(hydraOrGenericMessage(e) ?? DEFAULT_ERROR_MESSAGE)
        } finally {
            setCreatingExport(false)
        }
    }

    return (
        <>
            <Helmet>
                <title>Promotions Export | Peracto</title>
            </Helmet>
            <Heading name="Promotions Export">
                <div className="ml-2 d-flex align-items-center">
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={createExport}
                        disabled={creatingExport}
                        data-testid="generate-new-export"
                    >
                        <FontAwesomeIcon icon={faDownload} className="mr-2" />
                        {creatingExport ? 'Generating Export...' : 'Generate New Export'}
                    </button>
                </div>
            </Heading>

            <div className="row">
                <div className="col">
                    <div className="card card--transparent">
                        <div className="card-body">
                            <HydraTable
                                key={tableKey}
                                columns={columns}
                                selectedColumns={selectedColumns}
                                onFetchData={onFetchData}
                                onSelectColumn={onSelectColumn}
                                alwaysFilter={{ type: PROMOTIONS_API_IDENTIFIER }}
                                noResultsMessage={
                                    <p className="mb-0 text-muted">
                                        No promotions exports have been created
                                    </p>
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

function hydraOrGenericMessage(e) {
    return e?.error?.body?.hasOwnProperty('hydra:description')
        ? e.error.body['hydra:description']
        : null
}

export default PromotionsDataExportsList
